import { motion } from 'framer-motion';
import styled from 'styled-components';
import { TwoButton } from '../items/two.button';
import { Controller, useForm } from 'react-hook-form';
import { useState } from 'react';
import { postObtainsOrder } from '../../api/order';
import { AlertModal } from './alert.modal';
import { useHistory } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckListSchema } from '../../schema/orderSchema';

interface ConfirmModalProps {
  handleConfirmClose: () => void;
  onClick?: () => void;
  id: number;
  remainingVehicleLength: number;
  remainingVehicles: string[];
  setConfirmModal: any;
}

interface ObtainsOrderDto {
  vehicle: string[];
}

export const OrderConfirmModal = ({
  handleConfirmClose,
  id,
  remainingVehicleLength,
  remainingVehicles,
  setConfirmModal,
}: ConfirmModalProps) => {
  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CheckListSchema),
    defaultValues: {
      vehicle: [] as string[], // 빈 배열로 설정
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const orderData: ObtainsOrderDto = {
        vehicle: data.vehicle,
      };
      const response = await postObtainsOrder(id, orderData);

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else if (response.statusCode === 400) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage('수주에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error: any) {
      // 서버로부터 받은 에러 메시지를 처리
      if (error && error.status === 400) {
        // 서버에서 전달한 에러 메시지가 있는 경우
        if (error.data && error.data.message) {
          setModalMessage(error.data.message);
        } else {
          setModalMessage('이미 수주 되어 있습니다.');
        }
      } else {
        setModalMessage('오류가 발생했습니다.');
      }
      setIsModalOpen(true);
      setNavigateOnClose(false);
      console.error('Error submitting order:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/userOrder',
      });
    }
  };

  return (
    <ModalContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        type: 'fade',
      }}
    >
      <div className="overlay" onClick={handleConfirmClose}></div>
      <ModalInner
        className="wrapper"
        initial={{ y: '100%' }}
        animate={{ y: '0%' }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 40,
        }}
      >
        <div className="modal-body">
          <p>해당 건을 수주 하시겠습니까?</p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <RadioBtnBox>
              {remainingVehicles.map((item, i) => (
                <div key={i} className="radio-btn-box">
                  <Controller
                    control={control}
                    name="vehicle"
                    render={({ field }) => {
                      // Ensure field.value is always an array
                      const value = Array.isArray(field.value)
                        ? field.value
                        : [];
                      return (
                        <input
                          type="checkbox"
                          value={item}
                          id={item}
                          checked={value.includes(item)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (value.length < remainingVehicleLength) {
                                // 현재 선택된 차량의 수가 전체 차량의 수보다 적은 경우에만 추가
                                field.onChange([...value, item]);
                              }
                            } else {
                              field.onChange(value.filter((v) => v !== item));
                            }
                          }}
                        />
                      );
                    }}
                  />
                  <label htmlFor={item.toString()}>{item}</label>
                </div>
              ))}
              {errors.vehicle && (
                <ValTxt>{String(errors.vehicle.message)}</ValTxt>
              )}
            </RadioBtnBox>
          </form>
        </div>
        <TwoButton
          cancleTitle="취소"
          confirmTitle="확인"
          padding="1.3rem"
          onClick={handleSubmit(onSubmit)}
          confirmType="submit"
        />
      </ModalInner>
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </ModalContainer>
  );
};

// style
const ModalContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2.6rem;
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
  }
`;

const ModalInner = styled(motion.div)`
  position: relative;
  z-index: 99;
  padding: 5rem 1.9rem 1.5rem;
  background-color: ${(props) => props.theme.textWhiteColor};
  width: 100%;
  min-height: 23.4rem;
  border-radius: 1.6rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .modal-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 1.8rem;
    font-weight: 700;
    width: 100%;
    form {
      width: 100%;
    }
  }
`;

const RadioBtnBox = styled.div`
  margin-top: 2.8rem;
  margin-bottom: 2.3rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  .radio-btn-box {
    border-bottom: 1px solid ${(props) => props.theme.gray300};
    width: 100%;
    padding: 0.8rem 1.1rem;
    display: flex;
    align-items: center;
    input[type='checkbox'] {
      display: none;
    }
    label {
      width: 100%;
      display: flex;
      align-items: center;
      font-size: 1.6rem;
      font-weight: 600;
      line-height: 2.2rem;
      position: relative;
      &::after {
        content: '';
        width: 2.2rem;
        height: 2.2rem;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        background: url('/assets/check-ico-gray.svg');
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
      }
    }
    input[type='checkbox']:checked + label {
      &::after {
        background: url('/assets/check-ico-blue.svg');
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
      }
    }
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 1rem 1.7rem 0;
`;
